import React, { useState, useEffect, useRef, useCallback } from 'react';
import ListPageHeader from '../common/ListPageHeader.js';
import { useNavigate } from 'react-router-dom';
import Loader from "../common/Loader.js"
import { DashboardLayout } from '../common/page_component/index.js';
import Alert from '../common/Alert.js';
import companyService from '../../services/CompanyService.js';
import { Building2, Plus, X } from "lucide-react";
import ListFilterPanel from '../common/ListFilterPanel.js';
import Button from '../common/Button.js';
import RowActionsMenu from '../common/RowActionsMenu.js';
import DeleteCompanyModal from './DeleteCompanyModal.js';
import { NETWORK_MODE_LABELS } from '../../utils/companyFeatures.js';
import { useTranslation } from 'react-i18next';
import usePaginatedListFetch from '../../utils/usePaginatedListFetch.js';
import { mergeLoadedPage } from '../../utils/mergeLoadedPage.js';
import ListPagination from '../common/ListPagination.js';
import SearchSubmitInput from '../common/SearchSubmitInput.js';

const CompanyList = () => {
  const { t } = useTranslation();
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Separate filter states
  const [nameFilter, setNameFilter] = useState('');
  const [emailFilter, setEmailFilter] = useState('');
  const [contactFilter, setContactFilter] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const perPage = 10;
  
  const [activeDropdown, setActiveDropdown] = useState(null);
  const [purgeTarget, setPurgeTarget] = useState(null);
  const [exportingId, setExportingId] = useState(null);
  const [importingId, setImportingId] = useState(null);
  const importInputRef = useRef(null);
  const [importTargetId, setImportTargetId] = useState(null);
  const [invoicingId, setInvoicingId] = useState(null);
  const [emailingId, setEmailingId] = useState(null);
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  const fetchCompanies = useCallback(async () => {
    try {
      setLoading(true);
      const response = await companyService.getAll(currentPage, perPage, {
        company_name: nameFilter || undefined,
        email: emailFilter || undefined,
        contact: contactFilter || undefined,
        status: statusFilter || undefined,
      });
      setCompanies((prev) => mergeLoadedPage(currentPage, response.data || [], prev));
      setTotalPages(response.pagination?.pages || 1);
    } catch (err) {
      console.error('Error fetching companies:', err);
      Alert.error('Failed to load companies');
      console.error(err);
      if (currentPage <= 1) setCompanies([]);
    } finally {
      setLoading(false);
    }
  }, [currentPage, nameFilter, emailFilter, contactFilter, statusFilter]);

  usePaginatedListFetch({
    fetchPage: fetchCompanies,
    setCurrentPage,
    currentPage,
    resetPageDeps: [nameFilter, emailFilter, contactFilter, statusFilter],
    debounceWhen: nameFilter || emailFilter || contactFilter,
    debounceMs: 0,
  });

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setActiveDropdown(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleDeactivate = async (companyId, companyName) => {
    const confirmed = await Alert.confirm(
      `Deactivate "${companyName}"? All users under this company will also be deactivated.`,
      'Deactivate Company'
    );

    if (confirmed) {
      try {
        await companyService.deactivate(companyId);
        Alert.success('Company deactivated successfully!');
        fetchCompanies();
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to deactivate company');
      }
    }
  };

  const handleToggleStatus = async (companyId, companyName, currentStatus) => {
    const newStatus = currentStatus === 'active' ? 'inactive' : 'active';
    const confirmMessage = `Are you sure you want to ${newStatus === 'active' ? 'activate' : 'deactivate'} "${companyName}"?`;
    
    const confirmed = await Alert.confirm(confirmMessage, 'Toggle Status');
    
    if (confirmed) {
      try {
        if (newStatus === 'active') {
          await companyService.activate(companyId);
        } else {
          await companyService.deactivate(companyId);
        }
        Alert.success(`Company ${newStatus === 'active' ? 'activated' : 'deactivated'} successfully!`);
        fetchCompanies();
      } catch (err) {
        Alert.error(err.response?.data?.error || 'Failed to toggle company status');
      }
    }
  };

  const handleExportExcel = async (company) => {
    try {
      setExportingId(company.id);
      const { filename } = await companyService.exportExcel(company.id);
      Alert.success(`Downloaded ${filename}`);
    } catch (err) {
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const payload = JSON.parse(text);
          Alert.error(payload.error || 'Failed to export company data');
        } catch {
          Alert.error('Failed to export company data');
        }
      } else {
        Alert.error(err.response?.data?.error || 'Failed to export company data');
      }
    } finally {
      setExportingId(null);
      setActiveDropdown(null);
    }
  };

  const handleImportExcel = (company) => {
    setImportTargetId(company.id);
    setActiveDropdown(null);
    if (importInputRef.current) {
      importInputRef.current.value = '';
      importInputRef.current.click();
    }
  };

  const handleImportFile = async (event) => {
    const file = event.target.files?.[0];
    const companyId = importTargetId;
    if (!file || !companyId) return;
    try {
      setImportingId(companyId);
      const result = await companyService.importExcel(companyId, file);
      const failed = (result.sheets || []).reduce((sum, sheet) => sum + (sheet.failed || 0), 0);
      const added = (result.sheets || []).reduce((sum, sheet) => sum + (sheet.added || 0), 0);
      Alert.success(result.message || `Saved ${added} rows. ${failed} failed.`);
      fetchCompanies();
    } catch (err) {
      Alert.error(err.response?.data?.error || 'Failed to import company data');
    } finally {
      setImportingId(null);
      setImportTargetId(null);
    }
  };

  const handleDownloadInvoice = async (company) => {
    try {
      setInvoicingId(company.id);
      const { filename } = await companyService.downloadBillingInvoice(company.id);
      Alert.success(`Downloaded ${filename}`);
    } catch (err) {
      if (err.response?.data instanceof Blob) {
        try {
          const text = await err.response.data.text();
          const payload = JSON.parse(text);
          Alert.error(payload.error || 'Failed to download invoice');
        } catch {
          Alert.error('Failed to download invoice');
        }
      } else {
        Alert.error(err.response?.data?.error || 'Failed to download invoice');
      }
    } finally {
      setInvoicingId(null);
      setActiveDropdown(null);
    }
  };

  const handleEmailInvoice = async (company) => {
    if (!company?.email) {
      Alert.error('Company has no email address');
      setActiveDropdown(null);
      return;
    }
    const confirmed = await Alert.confirm(
      `Send dues invoice to ${company.email}?`,
      'Email Invoice'
    );
    if (!confirmed) {
      setActiveDropdown(null);
      return;
    }
    try {
      setEmailingId(company.id);
      const res = await companyService.sendBillingInvoiceEmail(company.id);
      Alert.success(res.message || `Invoice emailed to ${company.email}`);
    } catch (err) {
      Alert.error(err.response?.data?.error || err.response?.data?.message || 'Failed to email invoice');
    } finally {
      setEmailingId(null);
      setActiveDropdown(null);
    }
  };

  const toggleDropdown = (companyId) => {
    setActiveDropdown(activeDropdown === companyId ? null : companyId);
  };

  const getNetworkModeBadge = (networkMode) => {
    const isExternal = networkMode === 'EXTERNAL';
    return (
      <span className={`px-2 py-0.5 inline-flex text-xs leading-5 font-semibold rounded-full ${
        isExternal ? 'bg-purple-100 text-purple-800' : 'bg-slate-100 text-slate-700'
      }`}>
        {NETWORK_MODE_LABELS[networkMode] || 'Company Owner'}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-IN', { 
      year: 'numeric', 
      month: 'short', 
      day: 'numeric' 
    });
  };

  // Clear individual filter
  const clearFilter = (filterType) => {
    switch(filterType) {
      case 'name':
        setNameFilter('');
        break;
      case 'email':
        setEmailFilter('');
        break;
      case 'contact':
        setContactFilter('');
        break;
      case 'status':
        setStatusFilter('');
        break;
      default:
        break;
    }
  };

  // Clear all filters
  const clearAllFilters = () => {
    setNameFilter('');
    setEmailFilter('');
    setContactFilter('');
    setStatusFilter('');
  };

  const hasActiveFilters = nameFilter || emailFilter || contactFilter || statusFilter;

  return (
    <DashboardLayout>
              <input
                ref={importInputRef}
                type="file"
                accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,application/octet-stream"
                className="absolute h-px w-px opacity-0"
                onChange={handleImportFile}
              />
              <ListPageHeader
                title={t('pages.company.title')}
                icon={Building2}
                titleClassName="text-primary"
                iconClassName="w-6 h-6 sm:w-7 sm:h-7 text-primary shrink-0"
                className="mb-4 sm:mb-6"
              >
                <Button mobileIconOnly variant="primary" icon={Plus} onClick={() => navigate('/companies/add')}>
                  Add Company
                </Button>
              </ListPageHeader>

              <ListFilterPanel hasActiveFilters={hasActiveFilters} onClearFilters={clearAllFilters}>
                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Company Name
                  </label>
                  <SearchSubmitInput
                    value={nameFilter}
                    onSearch={setNameFilter}
                    placeholder="Search by name..."
                    inputClassName="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Email
                  </label>
                  <SearchSubmitInput
                    value={emailFilter}
                    onSearch={setEmailFilter}
                    placeholder="Search by email..."
                    inputClassName="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent text-sm"
                  />
                </div>

                <div>
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Contact Number
                  </label>
                  <SearchSubmitInput
                    value={contactFilter}
                    onSearch={setContactFilter}
                    placeholder="Search by contact..."
                    inputClassName="w-full px-3 py-2 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent text-sm"
                  />
                </div>

                <div className="relative">
                  <label className="block text-xs font-medium text-gray-700 mb-1">
                    Status
                  </label>
                  <div className="relative">
                    <select
                      value={statusFilter}
                      onChange={(e) => setStatusFilter(e.target.value)}
                      className="w-full px-3 py-2 pr-8 border border-gray-300 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-400 focus:border-transparent text-sm appearance-none"
                    >
                      <option value="">All Status</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                    {statusFilter && (
                      <button
                        onClick={() => clearFilter('status')}
                        className="absolute right-8 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 z-10"
                      >
                        <X className="w-4 h-4" />
                      </button>
                    )}
                    <div className="absolute right-2 top-1/2 -translate-y-1/2 pointer-events-none">
                      <svg className="w-4 h-4 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                      </svg>
                    </div>
                  </div>
                </div>
              </ListFilterPanel>

              <div className="bg-white rounded-xl shadow-sm">
                {loading && currentPage === 1 ? (
                  <div className="flex items-center justify-center py-20">
                    <Loader fullScreen={false} size="default" message="Loading companies..." />
                  </div>
                ) : (
                  <>
                {/* Table View - Desktop */}
                <div className="hidden lg:block">
                  {companies.length === 0 ? (
                    <div className="p-8 text-center text-gray-600">
                      {hasActiveFilters ? 'No companies match your filters' : 'No companies found'}
                    </div>
                  ) : (
                    <>
                      <div className="min-w-full">
                        <table className="min-w-full divide-y divide-gray-200">
                          <thead className="bg-gray-50">
                            <tr>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Logo
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Company Name
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Company ID
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Contact
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Location
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Status
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Validity Period
                              </th>
                              <th scope="col" className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                Actions
                              </th>
                            </tr>
                          </thead>
                          <tbody className="bg-white divide-y divide-gray-200">
                            {companies.map((company) => (
                              <tr key={company.id} className="hover:bg-gray-50 transition-colors">
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="w-12 h-12 rounded-xl overflow-hidden bg-gray-100 flex items-center justify-center">
                                    {company.logo_path ? (
                                      <img 
                                        src={company.logo_path} 
                                        alt={`${company.company_name} logo`}
                                        className="w-full h-full object-contain"
                                        onError={(e) => {
                                          e.target.style.display = 'none';
                                          e.target.nextSibling.style.display = 'flex';
                                        }}
                                      />
                                    ) : null}
                                    <div className={`w-full h-full flex items-center justify-center text-gray-400 ${company.logo_path ? 'hidden' : ''}`}>
                                      <Building2 className="w-6 h-6" />
                                    </div>
                                  </div>
                                </td>
                                <td className="px-6 py-4">
                                  <div
                                    className="text-sm font-medium text-gray-900 hover:text-blue-600 cursor-pointer transition-colors"
                                    onClick={() => navigate(`/companies/view/${company.id}`)}
                                  >
                                    {company.company_name}
                                  </div>
                                  <div className="flex flex-wrap items-center gap-2 mt-1.5">
                                    {company.company_code && (
                                      <span className="text-xs font-medium text-gray-500">
                                        {company.company_code}
                                      </span>
                                    )}
                                    {getNetworkModeBadge(company.network_mode)}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-gray-600">{company.company_id}</div>
                                </td>
                                <td className="px-6 py-4">
                                  <div className="text-sm text-gray-900">{company.contact || '—'}</div>
                                  <div className="text-xs text-gray-500 mt-0.5 break-all">{company.email || '—'}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-gray-600">{company.city}</div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <span className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${
                                    company.is_active 
                                      ? 'bg-green-100 text-green-800' 
                                      : 'bg-red-100 text-red-800'
                                  }`}>
                                    {company.is_active ? 'Active' : 'Inactive'}
                                  </span>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap">
                                  <div className="text-sm text-gray-600">
                                    {formatDate(company.from_date)} - {formatDate(company.to_date)}
                                  </div>
                                </td>
                                <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                                  <RowActionsMenu
                                    isOpen={activeDropdown === company.id}
                                    onToggle={() => toggleDropdown(company.id)}
                                    onClose={() => setActiveDropdown(null)}
                                    menuRef={dropdownRef}
                                    onView={() => navigate(`/companies/view/${company.id}`)}
                                    onEdit={() => navigate(`/companies/edit/${company.id}`)}
                                    onDelete={() => setPurgeTarget(company)}
                                    deleteLabel="Delete Company"
                                    extraItems={[
                                      {
                                        label: invoicingId === company.id ? 'Invoice...' : 'Invoice',
                                        icon: '📄',
                                        variant: 'primary',
                                        onClick: () => handleDownloadInvoice(company),
                                      },
                                      {
                                        label: emailingId === company.id ? 'Emailing...' : 'Email Invoice',
                                        icon: '✉️',
                                        variant: 'primary',
                                        onClick: () => handleEmailInvoice(company),
                                      },
                                      company.status === 'active'
                                        ? {
                                            label: 'Deactivate',
                                            icon: '🔄',
                                            variant: 'primary',
                                            onClick: () =>
                                              handleDeactivate(company.id, company.company_name),
                                          }
                                        : {
                                            label: 'Activate',
                                            icon: '🔄',
                                            variant: 'success',
                                            onClick: () =>
                                              handleToggleStatus(company.id, company.company_name, company.status),
                                          },
                                      {
                                        label:
                                          exportingId === company.id
                                            ? 'Downloading...'
                                            : 'Download Data (Excel)',
                                        icon: '📥',
                                        variant: 'default',
                                        onClick: () => handleExportExcel(company),
                                      },
                                      {
                                        label:
                                          importingId === company.id
                                            ? 'Importing...'
                                            : 'Import Data (Excel)',
                                        icon: '📤',
                                        variant: 'default',
                                        onClick: () => handleImportExcel(company),
                                      },
                                    ]}
                                  />
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>

                      <div className="bg-gray-50 px-6 py-4 border-t">
                        <div className="text-sm text-gray-700">
                          Showing {companies.length} compan{companies.length !== 1 ? 'ies' : 'y'}
                        </div>
                      </div>
                    </>
                  )}
                </div>

                {/* Card View - Mobile/Tablet */}
                <div className="lg:hidden space-y-3 sm:space-y-4 p-3 sm:p-4">
                  {companies.length === 0 ? (
                    <div className="bg-white p-8 rounded-xl shadow-sm text-center text-gray-600">
                      {hasActiveFilters ? 'No companies match your filters' : 'No companies found'}
                    </div>
                  ) : (
                    companies.map((company) => (
                      <div key={company.id} className="bg-white rounded-xl shadow-sm p-4 sm:p-5">
                        <div className="mb-3">
                          <div className="flex items-start gap-3 mb-2">
                            <div className="w-14 h-14 sm:w-16 sm:h-16 rounded-xl overflow-hidden bg-gray-100 flex-shrink-0 flex items-center justify-center">
                              {company.logo_path ? (
                                <img 
                                  src={company.logo_path} 
                                  alt={`${company.company_name} logo`}
                                  className="w-full h-full object-contain"
                                  onError={(e) => {
                                    e.target.style.display = 'none';
                                    e.target.nextSibling.style.display = 'flex';
                                  }}
                                />
                              ) : null}
                              <div className={`w-full h-full flex items-center justify-center text-gray-400 ${company.logo_path ? 'hidden' : ''}`}>
                                <Building2 className="w-8 h-8" />
                              </div>
                            </div>
                            <div className="flex-1 min-w-0">
                              <h3 
                                className="font-semibold text-gray-900 text-base sm:text-lg hover:text-blue-600 cursor-pointer transition-colors"
                                onClick={() => navigate(`/companies/view/${company.id}`)}
                              >
                                {company.company_name}
                              </h3>
                              <p className="text-xs sm:text-sm text-gray-600">ID: {company.company_id}</p>
                              <div className="flex flex-wrap items-center gap-2 mt-1">
                                {company.company_code && (
                                  <span className="text-xs font-medium text-gray-500">{company.company_code}</span>
                                )}
                                {getNetworkModeBadge(company.network_mode)}
                              </div>
                            </div>
                            <div className="flex items-center gap-2 shrink-0">
                              <span className={`px-2 sm:px-3 py-0.5 sm:py-1 rounded-full text-xs font-semibold ${
                                company.is_active
                                  ? 'bg-green-100 text-green-700'
                                  : 'bg-red-100 text-red-700'
                              }`}>
                                {company.is_active ? 'Active' : 'Inactive'}
                              </span>
                              <RowActionsMenu
                                isOpen={activeDropdown === company.id}
                                onToggle={() => toggleDropdown(company.id)}
                                onClose={() => setActiveDropdown(null)}
                                menuRef={dropdownRef}
                                onView={() => navigate(`/companies/view/${company.id}`)}
                                onEdit={() => navigate(`/companies/edit/${company.id}`)}
                                onDelete={() => setPurgeTarget(company)}
                                deleteLabel="Delete Company"
                                extraItems={[
                                  {
                                    label: invoicingId === company.id ? 'Invoice...' : 'Invoice',
                                    icon: '📄',
                                    variant: 'primary',
                                    onClick: () => handleDownloadInvoice(company),
                                  },
                                  {
                                    label: emailingId === company.id ? 'Emailing...' : 'Email Invoice',
                                    icon: '✉️',
                                    variant: 'primary',
                                    onClick: () => handleEmailInvoice(company),
                                  },
                                  company.status === 'active'
                                    ? {
                                        label: 'Deactivate',
                                        icon: '🔄',
                                        variant: 'primary',
                                        onClick: () =>
                                          handleDeactivate(company.id, company.company_name),
                                      }
                                    : {
                                        label: 'Activate',
                                        icon: '🔄',
                                        variant: 'success',
                                        onClick: () =>
                                          handleToggleStatus(company.id, company.company_name, company.status),
                                      },
                                  {
                                    label:
                                      exportingId === company.id
                                        ? 'Downloading...'
                                        : 'Download Data (Excel)',
                                    icon: '📥',
                                    variant: 'default',
                                    onClick: () => handleExportExcel(company),
                                  },
                                  {
                                    label:
                                      importingId === company.id
                                        ? 'Importing...'
                                        : 'Import Data (Excel)',
                                    icon: '📤',
                                    variant: 'default',
                                    onClick: () => handleImportExcel(company),
                                  },
                                ]}
                              />
                            </div>
                          </div>
                        </div>

                        <div className="grid grid-cols-2 gap-2 sm:gap-3 text-xs sm:text-sm">
                          <div>
                            <span className="text-gray-500">Contact:</span>
                            <p className="text-gray-900 font-medium">{company.contact || '—'}</p>
                            <p className="hidden md:block text-gray-600 text-xs break-all">{company.email || '—'}</p>
                          </div>
                          <div className="md:hidden">
                            <span className="text-gray-500">Email:</span>
                            <p className="text-gray-900 font-medium break-all text-xs">{company.email || '—'}</p>
                          </div>
                          <div>
                            <span className="text-gray-500">Location:</span>
                            <p className="text-gray-900 font-medium">{company.city || '—'}</p>
                          </div>
                          <div>
                            <span className="text-gray-500">Validity:</span>
                            <p className="text-gray-900 font-medium text-xs">
                              {formatDate(company.from_date)} - {formatDate(company.to_date)}
                            </p>
                          </div>
                        </div>
                      </div>
                    ))
                  )}
                </div>
                <ListPagination
                  currentPage={currentPage}
                  totalPages={totalPages}
                  loading={loading}
                  onNext={() => setCurrentPage((page) => page + 1)}
                />
                  </>
                )}
              </div>
      {purgeTarget && (
        <DeleteCompanyModal
          company={purgeTarget}
          onClose={() => setPurgeTarget(null)}
          onSuccess={fetchCompanies}
        />
      )}
    </DashboardLayout>
  );
};

export default CompanyList;